import { Component, OnInit, ViewChild, OnDestroy } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { ToastService } from "angular-toastify";
import { AdventureProgram } from "src/app/shared/models/adventure-program.model";
import { AdventureProgramStore } from "src/app/shared/services/stores/adventure-program.store.service";
import { AddAdventureProgramsDialog } from "./add-adventure-programs/add-adventure-programs.component";
import { DeleteConfirmationDialog } from "src/app/shared/components/delete-confirmation/delete-confirmation.component";
import { MatPaginator } from "@angular/material/paginator";
import { MatTableDataSource } from "@angular/material/table";
import { AccountStore } from "src/app/shared/services/stores/account.store.service";
import { IAccount } from "src/app/shared/models/account.model";
import { ContentTagStore } from "src/app/shared/services/stores/content.store.service";
import { IContentTag } from "src/app/shared/models/content-tags.model";
import { combineLatest, Subscription } from "rxjs";
import { Age, VALID_AGES, VALID_ARCADE_TYPES } from "src/app/shared/constants";
import { environment } from "src/environments/environment";

@Component({
	selector: "app-adventure-programs",
	templateUrl: "./adventure-programs.component.html",
	styleUrls: ["./adventure-programs.component.scss"],
})
export class AdventureProgramsComponent implements OnInit, OnDestroy {
	@ViewChild(MatPaginator) paginator!: MatPaginator;

	priorityLabels = ['Very Low Priority', 'Low Priority', 'Normal Priority', 'High Priority', 'Very High Priority'];
	priorityColors = ['#8B0000', '#FF0000', '#FFD700', '#90EE90', '#006400'];

	dataSource = new MatTableDataSource<AdventureProgram>();
	loading = false;
	enableEdit = true;
	enableDeletion = true;
	defaultPageSize = 10;
	pageSizeOptions = [5, 10, 20];
	defaultFilter = "";

	availableTags?: IContentTag[];
	private subscriptions = new Subscription();

	selectedAge: Age | null = null;
	ageOptions: Age[] = [...VALID_AGES];
	selectedStatus: boolean | null = null;
	selectedArcadeType: string | null = null;
	availableArcadeTypes: string[] = [];

	// Pagination properties
	currentPage = 0;
	totalItems = 0;
	paginatedPrograms: AdventureProgram[] = [];

	constructor(
		private adventureProgramStore: AdventureProgramStore,
		private dialog: MatDialog,
		private toastService: ToastService,
	) {
		this.defaultFilter = sessionStorage.getItem("adventureProgramFilter") ?? "";
		this.defaultPageSize = parseInt(sessionStorage.getItem("adventureProgramsPageSize") ?? "10");
	}

	ngOnInit(): void {
		this.loadData();
	}

	ngOnDestroy(): void {
		this.subscriptions.unsubscribe();
	}

	private loadData(): void {
		this.loading = true;
		const programs = this.adventureProgramStore.listPrograms();

		const sub = combineLatest([programs])
			.subscribe({
				next: ([programs]) => {
					if (programs === null) {
						return;
					}
					this.dataSource = new MatTableDataSource<AdventureProgram>(programs);

					// Coleta todos os tipos de arcade únicos dos programas
					const uniqueArcadeTypes = new Set<string>();

					programs.forEach(program => {
						if (!VALID_ARCADE_TYPES.includes(program.arcadeType as typeof VALID_ARCADE_TYPES[number])) {
							uniqueArcadeTypes.add(program.arcadeType);
						}
					});

					this.availableArcadeTypes = [...Array.from(uniqueArcadeTypes), ...VALID_ARCADE_TYPES.filter(type => type != "custom")];

					// Limpa os filtros
					this.selectedAge = null;
					this.selectedStatus = null;
					this.selectedArcadeType = null;
					this.defaultFilter = "";
					sessionStorage.removeItem("adventureProgramFilter");

					// Configura o filtro padrão
					this.setupFilterPredicate();
					this.dataSource.filter = this.defaultFilter;
					this.totalItems = programs.length;
					this.updatePaginatedData();
					this.loading = false;
				},
				error: () => {
					this.toastService.error("Error loading data");
					this.loading = false;
				}
			});
		this.subscriptions.add(sub);
	}

	private setupFilterPredicate(): void {
		this.dataSource.filterPredicate = (data: AdventureProgram, filter: string) => {
			// Filtro de texto (nome e descrição)
			const searchMatch = !this.defaultFilter ||
				data.name.toLowerCase().includes(this.defaultFilter.toLowerCase()) ||
				data.description.toLowerCase().includes(this.defaultFilter.toLowerCase());

			// Filtro de idade
			const ageMatch = !this.selectedAge ||
				data.ages.includes(this.selectedAge);

			// Filtro de status
			const statusMatch = this.selectedStatus === null ||
				data.isPublished === this.selectedStatus;

			// Filtro de tipo de arcade
			const arcadeTypeMatch = !this.selectedArcadeType ||
				data.arcadeType === this.selectedArcadeType;

			return searchMatch && ageMatch && statusMatch && arcadeTypeMatch;
		};
	}

	private updatePaginatedData(): void {
		const startIndex = this.currentPage * this.defaultPageSize;
		const endIndex = startIndex + this.defaultPageSize;
		this.paginatedPrograms = this.dataSource.filteredData.slice(startIndex, endIndex);
		this.totalItems = this.dataSource.filteredData.length;
	}

	paginatorChanged(event: any): void {
		this.currentPage = event.pageIndex;
		this.defaultPageSize = event.pageSize;
		sessionStorage.setItem('adventureProgramsPageSize', event.pageSize.toString());
		this.updatePaginatedData();
	}

	applyFilter(event: Event): void {
		const filterValue = (event.target as HTMLInputElement).value.trim().toLowerCase();
		this.defaultFilter = filterValue;
		this.dataSource.filter = filterValue;
		sessionStorage.setItem("adventureProgramFilter", filterValue);
		this.currentPage = 0;
		this.updatePaginatedData();
	}

	applyFilters(): void {
		this.setupFilterPredicate();
		this.dataSource.filter = this.defaultFilter || 'trigger';
		this.currentPage = 0;
		this.updatePaginatedData();
	}

	openProgramDialog(): void {
		const dialogRef = this.dialog.open(AddAdventureProgramsDialog);
		dialogRef.afterClosed().subscribe((result) => {
			if (result) {
				this.loadData();
			}
		});
	}

	editProgram(program: AdventureProgram): void {
		const dialogRef = this.dialog.open(AddAdventureProgramsDialog, {
			data: { program },
		});
		dialogRef.afterClosed().subscribe((result) => {
			if (result) {
				this.loadData();
			}
		});
	}

	deleteProgram(programId: string): void {
		const dialogRef = this.dialog.open(DeleteConfirmationDialog);
		dialogRef.afterClosed().subscribe((result) => {
			if (result) {
				this.adventureProgramStore.deleteProgram(programId);
			}
		});
	}

	createImage(id: string) {
		if (!id) {
			return `https://storage.googleapis.com/${environment.BUCKETS.CONTENT_BUCKET}/images/image_default.jpg`;
		}
		return `https://storage.googleapis.com/${environment.BUCKETS.CONTENT_BUCKET}/images/${id}.jpg`;
	}

	formatArcadeType(arcadeType: string): string {
		if (arcadeType === 'custom') {
			return 'CUSTOM';
		}
		return arcadeType
			.split('_')
			.map(word => word.charAt(0).toUpperCase() + word.slice(1))
			.join(' ');
	}
}
